import { Box, Button, Tab, Tabs } from '@mui/material';
import _ from 'lodash';
import type { SyntheticEvent } from 'react';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';

import VnModalBody from '@/modals/components/VnModalBody';
import VnModalFooter from '@/modals/components/VnModalFooter';
import { useModalContext } from '@/modals/VNModalProvider';
import { useAppDispatch } from '@/store';
import { AssetForm } from '@/store/forms';
import { SchemaType } from '@/store/forms/Asset-Editor/SchemaTypeOptions';
import { applicationDataSelector } from '@/store/slices/application-data';
import { fuelUserAccessSliceSelector } from '@/store/slices/fuelUserAccess';
import { batchUpload, SaveAssetJobsSelector } from '@/store/slices/saveAssetJobsState/SaveAssetJobsState';
import useAppUtils from '@/utils/appUtils';
import clientUtils from '@/utils/clientUtils';

import VNAccessSettings from '../VNAccessSettings/VNAccessSettings';
import VNUploadWidget from '../VNUploadWidget/VNUploadWidget';
import VideoForm from './forms/video';
import VNFileList from './VNFileList';

const DO_NOT_APPLY = { ingestFile: true, ingestFiles: true };
const DO_NOT_APPLY_MULTIPLE = { schemaType: true, title: true, synopsis: true, ingestFile: true, ingestFiles: true };
type UploadFrom = 'AssetsPage' | 'MoviesPage';

interface Iprops {
  uploadFrom?: UploadFrom;
  successCallBack: () => void;
}

function VNAssetUpload({ successCallBack = null, uploadFrom = 'AssetsPage' }: Iprops) {
  const [files, setFiles] = useState<File[]>([]);
  const [containsMov, setContainsMov] = useState(false);
  const [saving, setSaving] = useState(false);

  const { onClose, updateTitle } = useModalContext();

  const { alert } = useAppUtils();
  const { jobs } = useSelector(SaveAssetJobsSelector);
  const { ouData, categories } = useSelector(applicationDataSelector);
  const { permissions } = useSelector(fuelUserAccessSliceSelector);
  const dispatch = useAppDispatch();
  const [tab, setTab] = useState('details');
  const modal = useModalContext();

  class SelectableAsset extends AssetForm {
    selected: boolean;
  }

  const methods = useForm({
    defaultValues: AssetForm.create(permissions, ouData),
  });
  const { getValues, handleSubmit, setValue, watch } = methods;
  const includeAsTransition = watch('includeAsTransition');

  const methods2 = useForm({
    defaultValues: {
      filesToSave: [] as SelectableAsset[],
    },
  });
  const { getValues: getFiles, setValue: setFilesToSave } = methods2;

  useEffect(() => {
    if (files.length === 1) {
      const schemaType = clientUtils.getSchemaTypeByExtension(files[0], includeAsTransition);
      setValue('schemaType', schemaType);
      setValue('title', getFiles('filesToSave.0.title'));
      setValue('synopsis', getFiles('filesToSave.0.synopsis'));
    } else if (files.length > 1) {
      const schemaTypes: SchemaType[] = files.map((file) => clientUtils.getSchemaTypeByExtension(file, includeAsTransition));
      // console.log('schemaTypes', { schemaTypes });
      const uniqueSchemaTypes = _.uniq(schemaTypes);
      if (uniqueSchemaTypes.length === 1) {
        setValue('schemaType', uniqueSchemaTypes[0]);
      } else {
        setValue('schemaType', null);
      }
      setValue('title', null);
      setValue('synopsis', '');
    }
    if (files.length > 0) {
      modal.updateModalSize('xl');
      updateTitle(`Uploading ${files.length} files...`);
    } else {
      updateTitle('Upload');
      modal.updateModalSize('sm');
    }
  }, [files, includeAsTransition]);

  const base = 'body';
  const expanded = 'body expanded';

  const handleTabChange = async (event: SyntheticEvent, value: string) => {
    setTab(value);
  };

  return (
    <>
      <VnModalBody>
        <div className="vn-asset-upload">
          {files.length === 0 && (
            <div className="widget-container">
              <VNUploadWidget onFileChange={onFileChange} />
            </div>
          )}
          {files.length > 0 && (
            <div className="body expanded">
              <div className="column left">
                {files.length > 0 && (
                  <FormProvider {...methods2}>
                    <VNFileList files={files} onDelete={onDeleteFile} />
                  </FormProvider>
                )}
                <div className="widget-container">
                  <VNUploadWidget onFileChange={onFileChange} />
                </div>
              </div>
              {files.length > 0 && (
                <div className="column right">
                  <div className="tab-header-container">
                    <Box sx={{ borderBottom: 0, borderColor: 'divider' }}>
                      <Tabs value={tab} onChange={handleTabChange} aria-label="series tabs">
                        <Tab label="Details" value="details" />
                        <Tab label="Access" value="access-schedule" />
                        {/* <Tab label="Share" value="share" /> */}
                      </Tabs>
                    </Box>
                  </div>
                  <div className="form">
                    <FormProvider {...methods}>
                      {tab === 'details' && <VideoForm isMultiple={files.length > 1} hasMov={containsMov} isNew />}
                      {tab === 'access-schedule' && <VNAccessSettings name="accessSettings" />}
                    </FormProvider>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </VnModalBody>
      <VnModalFooter
        right={
          <>
            {files.length > 0 && (
              <Button variant="contained" color="success" size="small" onClick={handleSubmit(onSave)} disabled={saving}>
                Save
              </Button>
            )}
            <Button variant="contained" className="cancel-button" size="small" onClick={onCancel}>
              Cancel
            </Button>
          </>
        }
      />
    </>
  );

  // helper fns
  function onFileChange(fileList: FileList) {
    const isValid = clientUtils.validateFiles(fileList);
    if (!isValid) {
      alert('You can only upload video files (mp4, mov, avi, etc.) and image files (jpg, png, gif).', 'warning');
      return;
    }

    const newFiles = Array.from(fileList);
    const uniqueFiles = newFiles.filter((newFile) => !files.some((existingFile) => existingFile.name === newFile.name));

    const newForms = uniqueFiles.map((file) => {
      const f = SelectableAsset.createFromFile(file, ouData) as SelectableAsset;
      f.selected = true;
      return f;
    });

    const updatedFiles = [...files, ...uniqueFiles];
    const hasMov = updatedFiles.some((file) => file.name.toLowerCase().endsWith('.mov'));
    setContainsMov(hasMov);

    setFiles(updatedFiles);
    const filesToSave = getFiles('filesToSave');
    // in case user set singular file title or descrtiption
    if (filesToSave.length === 1) {
      filesToSave[0].title = getValues('title');
      filesToSave[0].synopsis = getValues('synopsis');
    }

    setFilesToSave('filesToSave', [...filesToSave, ...newForms]);
  }

  function onDeleteFile(event: Event, index: number) {
    event.stopPropagation();

    const updatedFiles = [...files.slice(0, index), ...files.slice(index + 1)];
    const hasMov = updatedFiles.some((file) => file.name.toLowerCase().endsWith('.mov'));
    setContainsMov(hasMov);
    const filesToSave = getFiles('filesToSave');
    const updatedFilesToSave = [...filesToSave.slice(0, index), ...filesToSave.slice(index + 1)];

    setFiles(updatedFiles);
    setFilesToSave('filesToSave', updatedFilesToSave);
  }

  function applyFormToFiles() {
    const update = getValues();
    const filesToSave = getFiles('filesToSave');

    const isMultiple = filesToSave.length > 1;

    const updatedFilesToSave = filesToSave.map((file) => {
      if (file.selected) {
        const updatedFile = { ...file };
        Object.keys(update).forEach((key) => {
          if (isMultiple && !DO_NOT_APPLY_MULTIPLE[key]) {
            updatedFile[key] = update[key];
          } else if (!isMultiple && !DO_NOT_APPLY[key]) {
            updatedFile[key] = update[key];
          }
        });
        if (update.common.categories && update.common.categories.length > 0) {
          updatedFile.categories = ouData.categories.filter((c) => update.common.categories.indexOf(c.id) > -1);
        }

        if (file.ingestFile?.name.toLowerCase().endsWith('.mov') && getValues('includeAsTransition')) {
          updatedFile.schemaType = SchemaType.Graphics;
        }

        return updatedFile;
      }

      return file;
    });

    setFilesToSave('filesToSave', updatedFilesToSave);
    return updatedFilesToSave;
  }

  function onSave() {
    if (saving) return;
    setSaving(true);
    const filesToSave = applyFormToFiles();

    const totalFiles = jobs.length + filesToSave.length;
    if (totalFiles > 100) {
      alert('You can upload only 100 assets at once.', 'warning');
      return;
    }

    const newAssetForms: AssetForm[] = [];

    // create forms for each new file
    for (let i = 0; i < filesToSave.length; i++) {
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      newAssetForms.push(filesToSave[i]);
    }

    dispatch(batchUpload({ forms: newAssetForms, jobType: 'BatchUpload' })).then((response) => {
      if (response && response.payload) {
        setSaving(false);
        const assetsSavedSuccessfully = response.payload.filter((r) => r.status === 'fulfilled');
        const errorCount = response.payload.filter((r) => r.status !== 'fulfilled');
        if (errorCount > 0) {
          alert(`Not all assets created - ${assetsSavedSuccessfully.length} created successfully, ${errorCount} failed.`);
        } else {
          alert(`All ${assetsSavedSuccessfully.length} Assets created successfully.`);
        }
        if (successCallBack) {
          setTimeout(() => {
            successCallBack();
          }, 5000);
        }
      }
    });

    onClose();
  }

  function onCancel() {
    onClose();
  }
}

export default VNAssetUpload;
